import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { useQueries } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';

import { CmsHistoryView, CmsMicrositePublicStatus } from '@ndvr/cms-api';

import { SimpleSortableHeader } from '@/components/SimpleSortableHeader';
import { Button } from '@/components/ui/button';
import { useSimpleTableSort } from '@/hooks/useSimpleTableSort';
import {
    MARKETING_CMS_NEW_SITE_PATH,
    MARKETING_CMS_REVIEWS_PATH,
    MARKETING_PATH,
    marketingCmsSiteHistoryPath,
} from '@/views/marketing/marketing-routes';
import { useAuthContext } from 'views/auth/Auth';

import { useCmsPermissions } from './cms-permissions';
import { cmsHistoryQueryOptions, useCmsMicrosites } from './cms-queries';
import { CmsPageHeader, CmsRelativeDateTime, CmsStatusBadge } from './cms-ui';
import { deriveCmsVersionRows, getActiveCmsCandidate } from './cms-version-state';

interface Props {
    microsites?: ReturnType<typeof useCmsMicrosites>['data'];
    isLoading?: boolean;
    isError?: boolean;
}

type CmsMicrosite = NonNullable<Props['microsites']>[number];
type CmsMicrositeSortColumn = 'advisor' | 'site' | 'public' | 'status' | 'updatedAt' | 'lastPublishedAt';

const getMicrositeWorkflowStatus = (microsite: CmsMicrosite, history?: CmsHistoryView): string => {
    const rows = deriveCmsVersionRows(history, {
        publishedContentVersionGUID: microsite.publishedContentVersionGUID,
    });
    const activeCandidate = getActiveCmsCandidate(rows);

    return activeCandidate?.displayStatus ?? rows[0]?.displayStatus ?? 'Draft';
};

const liveSiteUrl = (slug: string): string => `https://claroadvisors.com/advisors/${slug}`;

const advisorNameCollator = new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base',
});

const compareMicrositesByAdvisorName = (left: CmsMicrosite, right: CmsMicrosite) =>
    advisorNameCollator.compare(left.advisorDisplayName, right.advisorDisplayName) ||
    advisorNameCollator.compare(left.siteName, right.siteName);

const compareDateTime = (left?: string, right?: string) =>
    (left ? Date.parse(left) : 0) - (right ? Date.parse(right) : 0);

const tableHeaderClassName = 'h-[60px] px-2 text-xs font-medium uppercase';

export const CmsMicrositeList = ({
    microsites: providedMicrosites,
    isLoading: providedIsLoading,
    isError = false,
}: Props) => {
    const permissions = useCmsPermissions();
    const { user } = useAuthContext();
    const ownerUserGUID = permissions.canReview || permissions.canAdmin ? undefined : user?.profileGUID;
    const micrositesQuery = useCmsMicrosites(ownerUserGUID, { enabled: providedMicrosites === undefined });
    const advisorOrderedMicrosites = React.useMemo(
        () => [...(providedMicrosites ?? micrositesQuery.data ?? [])].sort(compareMicrositesByAdvisorName),
        [micrositesQuery.data, providedMicrosites],
    );
    const historyQueries = useQueries({
        queries: advisorOrderedMicrosites.map((microsite) => cmsHistoryQueryOptions(microsite.cmsMicrositeGUID)),
    });
    const workflowStatusesByMicrositeGUID = React.useMemo(() => {
        return new Map(
            advisorOrderedMicrosites.map((microsite, index) => [
                microsite.cmsMicrositeGUID,
                getMicrositeWorkflowStatus(microsite, historyQueries[index]?.data),
            ]),
        );
    }, [advisorOrderedMicrosites, historyQueries]);
    const sortFunctions = React.useMemo(
        () => ({
            advisor: compareMicrositesByAdvisorName,
            site: (left: CmsMicrosite, right: CmsMicrosite) =>
                advisorNameCollator.compare(left.siteName, right.siteName) ||
                compareMicrositesByAdvisorName(left, right),
            public: (left: CmsMicrosite, right: CmsMicrosite) =>
                advisorNameCollator.compare(left.publicStatus, right.publicStatus) ||
                compareMicrositesByAdvisorName(left, right),
            status: (left: CmsMicrosite, right: CmsMicrosite) =>
                advisorNameCollator.compare(
                    workflowStatusesByMicrositeGUID.get(left.cmsMicrositeGUID) ?? '',
                    workflowStatusesByMicrositeGUID.get(right.cmsMicrositeGUID) ?? '',
                ) || compareMicrositesByAdvisorName(left, right),
            updatedAt: (left: CmsMicrosite, right: CmsMicrosite) =>
                compareDateTime(left.updatedAt, right.updatedAt) || compareMicrositesByAdvisorName(left, right),
            lastPublishedAt: (left: CmsMicrosite, right: CmsMicrosite) =>
                compareDateTime(left.lastPublishedAt, right.lastPublishedAt) ||
                compareMicrositesByAdvisorName(left, right),
        }),
        [workflowStatusesByMicrositeGUID],
    );
    const {
        sortedData: sortedMicrosites,
        sortColumn,
        sortDirection,
        handleSort,
    } = useSimpleTableSort<CmsMicrosite, CmsMicrositeSortColumn>({
        data: advisorOrderedMicrosites,
        sortFunctions,
        initialSort: {
            column: 'advisor',
            direction: 'asc',
        },
    });
    const isLoading = providedIsLoading ?? micrositesQuery.isLoading;
    const hasError = isError || micrositesQuery.isError;

    return (
        <div className="flex flex-1 flex-col bg-white">
            <CmsPageHeader
                title="Advisor Microsites"
                parent={{ label: 'Marketing', to: MARKETING_PATH }}
                actions={
                    <>
                        {permissions.canReview && (
                            <Button asChild variant="outline2">
                                <Link to={MARKETING_CMS_REVIEWS_PATH}>Pending reviews</Link>
                            </Button>
                        )}
                        {permissions.canCreateMicrosite && (
                            <Button asChild variant="highlighted">
                                <Link to={MARKETING_CMS_NEW_SITE_PATH}>New microsite</Link>
                            </Button>
                        )}
                    </>
                }
            />
            <section>
                <div className="pt-4">
                    {isLoading ? (
                        <p className="text-muted-foreground px-3 text-sm">Loading CMS microsites...</p>
                    ) : hasError ? (
                        <p className="text-destructive px-3 text-sm">Unable to load CMS microsites.</p>
                    ) : sortedMicrosites.length === 0 ? (
                        <div className="mx-3 rounded-md p-8 text-center">
                            <p className="font-medium">No advisor microsites yet</p>
                            <p className="text-muted-foreground mt-1 text-sm">
                                Create the first microsite from Marketing when the backend grants access.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto px-8">
                            <table className="w-full min-w-[920px] table-fixed border-collapse text-sm font-light">
                                <thead>
                                    <tr className="text-left hover:bg-transparent">
                                        <SimpleSortableHeader
                                            column="advisor"
                                            label="Advisor"
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            className={tableHeaderClassName}
                                        />
                                        <SimpleSortableHeader
                                            column="site"
                                            label="Site"
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            className={tableHeaderClassName}
                                        />
                                        <SimpleSortableHeader
                                            column="public"
                                            label="Public"
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            className={tableHeaderClassName}
                                        />
                                        <SimpleSortableHeader
                                            column="status"
                                            label="Status"
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            className={tableHeaderClassName}
                                        />
                                        <SimpleSortableHeader
                                            column="updatedAt"
                                            label="Last updated"
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            className={tableHeaderClassName}
                                        />
                                        <SimpleSortableHeader
                                            column="lastPublishedAt"
                                            label="Last published"
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            className={tableHeaderClassName}
                                        />
                                        <th
                                            className="text-muted-foreground h-[60px] w-[190px] px-2 text-xs font-medium uppercase"
                                            aria-label="Actions"
                                        />
                                    </tr>
                                </thead>
                                <tbody className="[&>tr:last-child]:border-b">
                                    {sortedMicrosites.map((microsite) => {
                                        return (
                                            <tr
                                                key={microsite.cmsMicrositeGUID}
                                                className="hover:bg-muted/40 h-[60px] border-t align-middle"
                                            >
                                                <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                                                    {microsite.advisorDisplayName}
                                                </td>
                                                <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                                                    <div className="font-medium">{microsite.siteName}</div>
                                                    <div className="text-muted-foreground overflow-hidden font-mono text-xs text-ellipsis whitespace-nowrap">
                                                        /advisors/{microsite.slug}
                                                    </div>
                                                </td>
                                                <td className="px-2">
                                                    <CmsStatusBadge status={microsite.publicStatus} />
                                                </td>
                                                <td className="px-2">
                                                    <CmsStatusBadge
                                                        status={workflowStatusesByMicrositeGUID.get(
                                                            microsite.cmsMicrositeGUID,
                                                        )}
                                                    />
                                                </td>
                                                <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                                                    <CmsRelativeDateTime value={microsite.updatedAt} />
                                                </td>
                                                <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                                                    <CmsRelativeDateTime value={microsite.lastPublishedAt} />
                                                </td>
                                                <td className="px-2">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {microsite.publicStatus ===
                                                            CmsMicrositePublicStatus.PUBLISHED && (
                                                            <Button asChild variant="outline2" size="sm">
                                                                <a
                                                                    href={liveSiteUrl(microsite.slug)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    <ExternalLinkIcon className="h-4 w-4" />
                                                                    Live Site
                                                                </a>
                                                            </Button>
                                                        )}
                                                        <Button asChild variant="outline2" size="sm">
                                                            <Link
                                                                to={marketingCmsSiteHistoryPath(
                                                                    microsite.cmsMicrositeGUID,
                                                                )}
                                                            >
                                                                Manage
                                                            </Link>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};
